import { generateId } from '@databases/utils/utils';
import { DependencyList, useCallback, useMemo } from 'react';

import { ErrorSettings, flagErrors } from '../flag-errors';
import { removeFlag } from '../flags';

/**
 * Returns a wrapper for the given callback that shows an error flag for any occurring sync or async exceptions.
 */
export function flaggingCallback<T extends (...args: any[]) => any>(
  callback: T,
  settings?: ErrorSettings
): T {
  return ((...args: any) => {
    if (settings?.id) {
      removeFlag(settings.id);
    }

    return flagErrors(() => callback(...args), settings);
  }) as any;
}

/**
 * Like useCallback(), but an error flag is shown for any sync or async exceptions.
 */
export function useFlaggingCallback<T extends (...args: any[]) => any>(
  callback: T,
  deps: DependencyList,
  settings?: ErrorSettings
): T {
  // Use a fixed error ID so that calling the callback again will clear the last flag
  const id = useMemo(() => generateId(), []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useCallback(flaggingCallback(callback, { id, ...settings }), deps);
}
